import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, LogOut, Menu, User as UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/services", label: "Services", testId: "nav-services" },
  { to: "/proof", label: "Proof", testId: "nav-proof" },
  { to: "/insights", label: "Insights", testId: "nav-insights" },
  { to: "/about", label: "About", testId: "nav-about" },
  { to: "/inquiry", label: "Custom project", testId: "nav-inquiry" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/notifications/me");
      setNotifs(res.data);
    } catch (_err) {
      /* Header polls every 15s, so a transient failure self-heals. */
    }
  }, [user]);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 15000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  const goAndClose = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="header-logo">
          <div className="w-7 h-7 rounded-lg bg-[#0b1e5a] flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 64 64" className="w-full h-full">
              <text x="29" y="48" textAnchor="middle" fontFamily="Inter, system-ui, -apple-system, sans-serif" fontWeight="700" fontSize="48" fill="#ffffff" letterSpacing="-2">s</text>
              <circle cx="48" cy="44" r="6" fill="#ac62ea" />
            </svg>
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">SideB</span>
          <span className="hidden lg:inline-block text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground border-l border-border pl-2.5 ml-1.5">AI-First Consulting Studio</span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="text-sm font-medium text-foreground/80 hover:text-[#ac62ea] transition-colors" data-testid={l.testId}>{l.label}</Link>
          ))}
          {user && (
            <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="text-sm font-medium text-foreground/80 hover:text-[#ac62ea] transition-colors" data-testid="nav-dashboard">Dashboard</Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" className="text-sm font-medium text-[#ac62ea] hover:underline" data-testid="nav-admin">Admin</Link>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            onClick={() => navigate("/contact")}
            variant="outline"
            className="hidden sm:inline-flex rounded-full h-9 px-4 text-sm font-medium border-[#ac62ea]/30 text-[#ac62ea] hover:bg-[#ac62ea]/10 hover:text-[#ac62ea] hover:border-[#ac62ea]/50"
            data-testid="header-talk-to-us-btn"
          >
            Talk to us
          </Button>
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notif-trigger">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ac62ea] text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notif-badge">{unread}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 rounded-xl" data-testid="notif-menu">
                  <DropdownMenuLabel className="flex justify-between items-center">
                    <span>Notifications</span>
                    {unread > 0 && (
                      <button className="text-xs text-[#ac62ea] hover:underline" onClick={markAllRead} data-testid="notif-mark-read">Mark all read</button>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground">No notifications yet.</div>}
                  {notifs.slice(0, 8).map((n) => (
                    <DropdownMenuItem key={n.id} onClick={() => n.link && navigate(n.link)} className="flex flex-col items-start py-2.5 cursor-pointer">
                      <div className="flex items-center gap-2 w-full">
                        {!n.read && <span className="w-1.5 h-1.5 bg-[#ac62ea] rounded-full" />}
                        <span className="font-medium text-sm">{n.title}</span>
                      </div>
                      <span className="text-xs text-muted-foreground mt-0.5">{n.body}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-border" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#0b1e5a] text-white flex items-center justify-center text-xs">{user.name?.[0]}</div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl w-56">
                  <DropdownMenuLabel className="flex flex-col">
                    <span className="text-sm">{user.name}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                    {user.role === "admin" && (
                      <span className="mt-1 text-[10px] font-semibold tracking-widest text-[#ac62ea]">ADMIN</span>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(user?.role === "admin" ? "/admin" : "/dashboard")} data-testid="menu-dashboard">
                    <UserIcon className="h-4 w-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button onClick={() => navigate("/login")} className="rounded-full bg-[#0b1e5a] hover:bg-black h-9 px-4 sm:px-5 text-sm" data-testid="login-btn">
              Sign in
            </Button>
          )}

          {/* Mobile hamburger — visible below md */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu-trigger" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0 flex flex-col" data-testid="mobile-menu">
              <SheetTitle className="px-6 pt-6 text-base font-display tracking-tight">Menu</SheetTitle>
              <nav className="flex flex-col gap-1 px-3 mt-4">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.to}
                    onClick={() => goAndClose(l.to)}
                    className="text-left px-3 py-3 rounded-md text-[15px] font-medium hover:bg-muted transition-colors"
                    data-testid={`mobile-${l.testId}`}
                  >
                    {l.label}
                  </button>
                ))}
                {user && (
                  <button
                    onClick={() => goAndClose(user.role === "admin" ? "/admin" : "/dashboard")}
                    className="text-left px-3 py-3 rounded-md text-[15px] font-medium hover:bg-muted transition-colors"
                    data-testid="mobile-nav-dashboard"
                  >
                    Dashboard
                  </button>
                )}
                {user?.role === "admin" && (
                  <button
                    onClick={() => goAndClose("/admin")}
                    className="text-left px-3 py-3 rounded-md text-[15px] font-medium text-[#ac62ea] hover:bg-[#ac62ea]/10 transition-colors"
                    data-testid="mobile-nav-admin"
                  >
                    Admin console
                  </button>
                )}
              </nav>
              <div className="mt-auto p-6 border-t border-border">
                {user ? (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">Signed in as</div>
                    <div className="text-sm font-medium truncate">{user.email}</div>
                    <Button onClick={() => { setMobileOpen(false); logout(); }} variant="outline" className="w-full mt-3" data-testid="mobile-logout">
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </Button>
                  </div>
                ) : (
                  <Button onClick={() => goAndClose("/login")} className="w-full rounded-full bg-[#0b1e5a] hover:bg-black" data-testid="mobile-login-btn">
                    Sign in
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
